import { FC, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import CaresPageHeader from '../shell/cares-page-header';
import PortfolioSummaryCard from '../shared/portfolio-summary-card';
import { fetchCompanies, type CompanyRecord } from './clients/api';

const lifecycle = (company: CompanyRecord): { label: string; tone: string } => {
  if (company.status === 'archived') return { label: 'Archived', tone: 'dark' };
  if (company.status === 'active') return { label: 'Active', tone: 'success' };
  if (company.status === 'onboarding') return { label: 'Onboarding', tone: 'warning' };
  return { label: 'Draft', tone: 'secondary' };
};

const AdminOverviewPage: FC = () => {
  const [companies, setCompanies] = useState<CompanyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchCompanies();
      setCompanies(response.data.data);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load the client portfolio.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <>
      <CaresPageHeader homepage="CARES Administration" activepage="Admin" page="Overview" />

      {error ? (
        <Alert variant="danger" className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <span>{error}</span>
          <Button variant="danger" onClick={() => void load()}>Retry</Button>
        </Alert>
      ) : null}

      {loading ? (
        <Card className="custom-card">
          <Card.Body className="p-4 d-flex align-items-center gap-3 text-muted">
            <Spinner animation="border" size="sm" /> Loading client portfolio…
          </Card.Body>
        </Card>
      ) : companies.length === 0 ? (
        <Alert variant="warning">No clients are configured yet. Open Clients to provision the first client.</Alert>
      ) : (
        <Row className="g-3 cares-portfolio-grid">
          {companies.map((company) => {
            const status = lifecycle(company);
            const blockerCount = company.readiness.blockers.length;
            return (
              <Col sm={6} xl={4} xxl={3} key={company.id}>
                <PortfolioSummaryCard
                  title={company.name}
                  subtitle={company.slug}
                  status={status.label}
                  statusTone={status.tone}
                  actionTo={`/admin/clients?company=${encodeURIComponent(company.slug)}`}
                  actionLabel="Open client"
                  readiness={{
                    label: blockerCount ? `${blockerCount} blocker${blockerCount === 1 ? '' : 's'}` : 'Ready',
                    tone: blockerCount ? 'warning' : 'success',
                  }}
                  rows={[
                    { label: 'Locations', total: company.counts.locations_total, active: company.counts.locations_active, onboarding: '—', pending: '—' },
                    { label: 'Departments', total: company.counts.groups_total, active: company.counts.groups_active, onboarding: '—', pending: '—' },
                    { label: 'Providers', total: company.counts.providers_total, active: company.counts.providers_active, onboarding: '—', pending: '—' },
                  ]}
                />
              </Col>
            );
          })}
        </Row>
      )}
    </>
  );
};

export default AdminOverviewPage;
